import React, { lazy, useEffect, useState } from 'react';
import LazyModalBoundary from '@/components/LazyModalBoundary.tsx';
import {
  ActionIcon,
  Badge,
  Group,
  Menu,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Timeline,
} from '@mantine/core';
import { formatCalendarStyle } from '@/utils/datetime.ts';
import { HiDotsVertical } from 'react-icons/hi';
import StyledMenu from '@/components/StyledMenu/StyledMenu.tsx';
import useRevertAutomationVersion from '@/hooks/api/automationVersions/useRevertAutomationVersion.ts';
import useUpdateAutomationVersion from '@/hooks/api/automationVersions/useUpdateAutomationVersion.ts';
import ConfirmationModal from '@/components/ConfirmationModal.tsx';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import ModalConfirmationButtons from '@/components/StyledModal/ModalConfirmationButtons.tsx';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import ExpandableContent from '@/components/ExpandableContent.tsx';
import useDiscardLatestDraft from '@/hooks/api/automations/useDiscardLatestDraft.ts';
import {
  AutomationVersionSimple,
  getAutomationVersionDisplayName,
  getAutomationVersionLabel,
} from '@/types/automations.ts';
import VersionComparisonModal from '@/pages/Editor/components/VersionHistory/VersionComparison/VersionComparisonModal.tsx';
import VersionTestSuiteModal from '@/pages/Editor/components/VersionHistory/VersionTestSuiteModal.tsx';
import { useVersionHistoryReview } from '@/pages/Editor/components/VersionHistory/VersionHistoryModal/VersionHistoryReviewContext.ts';
import { useEditVersionContext } from '@/context/EditVersionContext/useEditVersionContext.ts';
import { notifications } from '@mantine/notifications';
import { useNavigate, useParams } from 'react-router-dom';

const VersionDocumentationModal = lazy(
  () => import('@/pages/Editor/components/VersionHistory/VersionDocumentationModal.tsx'),
);
const LocalTestEvidenceModal = lazy(
  () => import('@/pages/Editor/components/VersionHistory/LocalTestEvidenceModal.tsx'),
);

type props = {
  version: AutomationVersionSimple;
  publishedVersion?: AutomationVersionSimple;
  latestVersion?: AutomationVersionSimple;
  publishedVersions: boolean; // true if this is the published versions timeline
};

const creationKindLabels: Partial<Record<AutomationVersionSimple['creation_kind'], string>> = {
  initial: 'Initial version',
  forge_authoring: 'Forge draft',
  editor_edit: 'Editor edit',
  model_settings: 'Model settings',
  test_suite: 'Regression suite',
  local_test_package: 'Local test package',
  sensitive_setting: 'Sensitive setting',
  revert: 'Revert',
};

const VersionTimelineItem = ({
  version,
  publishedVersion,
  latestVersion,
  publishedVersions,
}: props) => {
  const { mutate: revertVersion, isPending: revertingVersion } = useRevertAutomationVersion();
  const { mutate: renameVersion, isPending: renamingVersion } = useUpdateAutomationVersion();
  const { mutate: discardLatestDraft, isPending: discardingLatestDraft } = useDiscardLatestDraft();
  const openHistoryReview = useVersionHistoryReview();
  const { hasPendingEdits, reloadEditor, staleDraft } = useEditVersionContext();
  const navigate = useNavigate();
  const { automationId } = useParams();

  const [versionToRevert, setVersionToRevert] = useState<AutomationVersionSimple | null>(null);
  const [versionToRename, setVersionToRename] = useState<AutomationVersionSimple | null>(null);
  const [versionToDiscard, setVersionToDiscard] = useState<AutomationVersionSimple | null>(null);
  const [versionToCompare, setVersionToCompare] = useState<AutomationVersionSimple | null>(null);
  const [versionToManageTests, setVersionToManageTests] = useState<AutomationVersionSimple | null>(
    null,
  );
  const [versionToDocument, setVersionToDocument] = useState<AutomationVersionSimple | null>(null);
  const [versionToViewLocalTestEvidence, setVersionToViewLocalTestEvidence] =
    useState<AutomationVersionSimple | null>(null);
  const [returnToRevert, setReturnToRevert] = useState(false);
  const [menuOpened, setMenuOpened] = useState(false);

  const openAdvancedRelease = (selected: AutomationVersionSimple) => {
    if (!automationId) return;
    navigate(`/automations/${automationId}/release?version=${encodeURIComponent(selected.id)}`);
  };

  const [versionName, setVersionName] = useState<string>('');

  useEffect(() => {
    if (versionToRename) {
      setVersionName(versionToRename.name || '');
    }
  }, [versionToRename]);

  const pending = revertingVersion || renamingVersion || discardingLatestDraft;
  const canRevertToVersion = version.id !== latestVersion?.id;
  const canDiscardCurrentDraft = !!(
    !publishedVersions &&
    publishedVersion &&
    version.id === latestVersion?.id &&
    !version.published_at &&
    latestVersion.id !== publishedVersion.id
  );
  const discardBlocked = hasPendingEdits || !!staleDraft || discardingLatestDraft;
  const hasMutableVersionActions =
    publishedVersions || version.id !== publishedVersion?.id || canRevertToVersion;
  const versionLabel = getAutomationVersionLabel(version) || 'Version';
  const customName = version.name.trim();
  const showCustomName = customName.length > 0 && customName !== versionLabel;
  const creationKindLabel = creationKindLabels[version.creation_kind];
  const hasMetadata = !!creationKindLabel || !!version.documentation_summary;

  return (
    <>
      <Timeline.Item
        key={version.id}
        bullet={<ThemeIcon size={8} radius="xl" color="#D9E3FE" />}
        style={{ paddingTop: '2px', paddingLeft: '6px', marginTop: '16px' }}
      >
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs" align="baseline" wrap="wrap">
              <Text size="sm" fw={500} style={{ lineHeight: 1.4 }}>
                {versionLabel}
              </Text>
              <Text size="xs" c="dimmed">
                {formatCalendarStyle(publishedVersions ? version.published_at : version.created_at)}
              </Text>
            </Group>
            {showCustomName ? (
              <Text size="xs" c={publishedVersions ? '#0F172A' : 'dimmed'} truncate="end">
                {customName}
              </Text>
            ) : null}
            {hasMetadata && (
              <Group gap="xs" align="center" wrap="wrap">
                {creationKindLabel && (
                  <Text size="xs" c="dimmed">
                    {creationKindLabel}
                  </Text>
                )}
                {version.documentation_summary && (
                  <Badge
                    size="xs"
                    color={
                      version.documentation_summary.alignment_status === 'reviewed_aligned'
                        ? 'green'
                        : version.documentation_summary.alignment_status === 'review_needed'
                          ? 'yellow'
                          : 'gray'
                    }
                    variant="light"
                  >
                    {version.documentation_summary.alignment_status === 'reviewed_aligned'
                      ? 'Blueprint reviewed'
                      : version.documentation_summary.alignment_status === 'review_needed'
                        ? 'Blueprint needs review'
                        : 'Blueprint missing'}
                  </Badge>
                )}
              </Group>
            )}
            {version.change_note && (
              <ExpandableContent
                maxHeight={36}
                fadeHeight={12}
                showLabel="Show full note"
                hideLabel="Show less"
              >
                <Text
                  size="xs"
                  c="dimmed"
                  style={{ lineHeight: '18px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                >
                  {version.change_note}
                </Text>
              </ExpandableContent>
            )}
          </Stack>

          <Group gap="xs" wrap="nowrap" align="center" style={{ flexShrink: 0 }}>
            {version.id === publishedVersion?.id && (
              <Badge color="blue" size="xs">
                Published
              </Badge>
            )}
            {version.id === latestVersion?.id && (
              <Badge color="primary" size="xs">
                Latest
              </Badge>
            )}
            <StyledMenu
              opened={menuOpened}
              onChange={setMenuOpened}
              position="bottom-end"
              clickOutsideEvents={['mouseup', 'touchend']}
            >
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="xs"
                  loading={pending}
                  aria-label="Version actions"
                >
                  <HiDotsVertical />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => setVersionToDocument(version)}>View blueprint</Menu.Item>
                <Menu.Item onClick={() => setVersionToViewLocalTestEvidence(version)}>
                  View local test evidence
                </Menu.Item>
                {hasMutableVersionActions && (
                  <>
                    {publishedVersions && (
                      <Menu.Item onClick={() => setVersionToRename(version)}>
                        Rename version
                      </Menu.Item>
                    )}
                    <Menu.Item
                      disabled={hasPendingEdits || Boolean(staleDraft)}
                      onClick={() => openAdvancedRelease(version)}
                    >
                      Review exact composition publication
                    </Menu.Item>
                    {canDiscardCurrentDraft && (
                      <Menu.Item
                        color="red"
                        disabled={discardBlocked}
                        onClick={() => setVersionToDiscard(version)}
                      >
                        Discard current draft
                      </Menu.Item>
                    )}
                    {publishedVersion && version.id !== publishedVersion.id && (
                      <Menu.Item
                        onClick={() =>
                          openHistoryReview
                            ? openHistoryReview(version)
                            : setVersionToCompare(version)
                        }
                      >
                        Compare with live version
                      </Menu.Item>
                    )}
                    {version.id === latestVersion?.id && (
                      <Menu.Item onClick={() => setVersionToManageTests(version)}>
                        Diagnostics and regression cases
                      </Menu.Item>
                    )}
                    {canRevertToVersion && (
                      <Menu.Item onClick={() => setVersionToRevert(version)}>
                        Revert to version
                      </Menu.Item>
                    )}
                  </>
                )}
              </Menu.Dropdown>
            </StyledMenu>
          </Group>
        </Group>
      </Timeline.Item>

      {versionToManageTests && (
        <VersionTestSuiteModal
          opened
          onClose={() => setVersionToManageTests(null)}
          version={versionToManageTests}
        />
      )}
      {versionToDocument && (
        <LazyModalBoundary
          resetKey={versionToDocument.id}
          title="Version blueprint"
          onClose={() => setVersionToDocument(null)}
        >
          <VersionDocumentationModal
            version={versionToDocument}
            onClose={() => setVersionToDocument(null)}
          />
        </LazyModalBoundary>
      )}
      {versionToViewLocalTestEvidence && (
        <LazyModalBoundary
          resetKey={versionToViewLocalTestEvidence.id}
          title="Local test evidence"
          onClose={() => setVersionToViewLocalTestEvidence(null)}
        >
          <LocalTestEvidenceModal
            version={versionToViewLocalTestEvidence}
            onClose={() => setVersionToViewLocalTestEvidence(null)}
          />
        </LazyModalBoundary>
      )}
      <ConfirmationModal
        opened={!!versionToRevert}
        onClose={() => setVersionToRevert(null)}
        title={`Revert to ${getAutomationVersionLabel(versionToRevert) || 'Version'}`}
        message={
          <Stack gap="xs">
            <Text size="sm">
              This creates a new draft based on {getAutomationVersionDisplayName(versionToRevert)}.
              Your version history remains intact.
            </Text>
            {publishedVersion && versionToRevert && publishedVersion.id !== versionToRevert.id && (
              <StyledButton
                variant="subtle"
                size="sm"
                w="fit-content"
                onClick={() => {
                  setVersionToRevert(null);
                  setVersionToCompare(versionToRevert);
                  setReturnToRevert(true);
                }}
              >
                Review changes from live version
              </StyledButton>
            )}
          </Stack>
        }
        onConfirm={() => revertVersion({ versionId: versionToRevert!.id })}
        confirmText={'Revert'}
      />

      <ConfirmationModal
        opened={!!versionToDiscard}
        onClose={() => setVersionToDiscard(null)}
        title="Discard current draft?"
        message={
          <Stack gap="xs">
            <Text size="sm">
              Discard current draft {getAutomationVersionLabel(versionToDiscard) || 'Version'} and
              restore published version {getAutomationVersionLabel(publishedVersion) || 'Version'}{' '}
              as the active version.
            </Text>
            <Text size="sm">Older historical drafts may remain in version history.</Text>
          </Stack>
        }
        confirmText="Discard draft and restore published version"
        confirmType="delete"
        confirmLoading={discardingLatestDraft}
        onConfirm={() => {
          if (hasPendingEdits || staleDraft) {
            notifications.show({
              message: hasPendingEdits
                ? 'Wait for pending changes to finish saving before discarding this draft.'
                : 'Reload the editor before discarding a draft that changed remotely.',
              color: 'yellow',
            });
            return;
          }
          discardLatestDraft(
            {
              draftVersionId: versionToDiscard!.id,
              expectedPublishedVersionId: publishedVersion!.id,
            },
            {
              onError: () => {
                setVersionToDiscard(null);
                notifications.show({
                  title: 'Draft not discarded',
                  message:
                    'Version history was refreshed. Review the current version state before trying again.',
                  color: 'yellow',
                });
              },
              onSuccess: reloadEditor,
            },
          );
        }}
      />

      {versionToCompare && (
        <VersionComparisonModal
          opened={!!versionToCompare}
          onClose={() => {
            const comparedVersion = versionToCompare;
            setVersionToCompare(null);
            if (returnToRevert && comparedVersion) setVersionToRevert(comparedVersion);
            setReturnToRevert(false);
          }}
          publishedVersion={publishedVersion}
          latestVersion={versionToCompare}
          initialBaseVersionId={publishedVersion?.id}
          initialTargetVersionId={versionToCompare?.id}
        />
      )}

      <StyledModal
        opened={!!versionToRename}
        onClose={() => setVersionToRename(null)}
        title={`Rename ${getAutomationVersionLabel(versionToRename) || 'Automation Version'}`}
        centered
      >
        <Stack gap="sm">
          <Text size="sm">Add or update the custom name for this version:</Text>
          <TextInput
            radius="md"
            placeholder="Optional custom name"
            size="sm"
            value={versionName}
            onChange={(e) => setVersionName(e.target.value)}
            autoFocus={true}
            data-autofocus
          />
        </Stack>
        <ModalConfirmationButtons
          onClose={() => setVersionToRename(null)}
          onConfirm={() => renameVersion({ versionId: versionToRename!.id, name: versionName })}
          confirmText={'Save'}
        />
      </StyledModal>
    </>
  );
};

export default VersionTimelineItem;
